import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Briefcase,
  CheckCircle,
  XCircle,
  Send,
  Ban,
  History,
  Edit,
  GitBranch,
  AlertTriangle,
  ExternalLink,
  ShieldAlert,
  UserPlus,
  UserMinus,
  ArrowLeftRight,
  Undo2,
} from 'lucide-react';
import toast from '../../utils/toast';
import { StatusBadge } from '../UI/StatusBadge';
import { InfoBadge } from '../UI/InfoBadge';
import AssignedPersonnelSection from '../AssignedPersonnelSection';
import { formatModificationType } from '../../utils/workforceRequestLabels';
import { staffAPI } from '../../services/api';
import NightworkHRActionModal from './NightworkHRActionModal';
import { getTodayInSingaporeTimezone, formatDateRange } from '../../utils/dateUtils';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso.includes('T') ? iso : iso + 'T00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Singapore' });
};

const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const formatDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Singapore',
  });
};

const timePart = (value) => value?.substring(0, 5) || null;

const isNoOpModification = (log) => {
  const details = log.details || {};
  if (log.modification_type === 'hr_adjust_times') {
    return (
      timePart(details.original_start_time) === timePart(details.new_start_time) &&
      timePart(details.original_end_time) === timePart(details.new_end_time)
    );
  }
  if (log.modification_type === 'hr_adjust_dates') {
    const hasRangeFields = (
      'original_start' in details || 'new_start' in details ||
      'original_end' in details || 'new_end' in details
    );
    const hasSpecificFields = 'original_dates' in details || 'new_dates' in details;
    const sameRange = (
      hasRangeFields &&
      details.original_start === details.new_start &&
      details.original_end === details.new_end
    );
    const sameSpecific = (
      hasSpecificFields &&
      JSON.stringify(details.original_dates || null) === JSON.stringify(details.new_dates || null)
    );
    return sameRange || sameSpecific;
  }
  return false;
};

const NightworkRequestDetail = ({ requestId, onBack, currentUser, onViewDetail, onEditAmendment }) => {
  const queryClient = useQueryClient();
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [approvalComments, setApprovalComments] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [returnComments, setReturnComments] = useState('');
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [hrActionType, setHrActionType] = useState(null);

  const { data: request, isLoading, error } = useQuery({
    queryKey: ['nightwork-request', requestId],
    queryFn: () => staffAPI.getNightworkRequest(requestId),
    select: r => r.data,
    staleTime: 15_000,
  });

  const submitMutation = useMutation({
    mutationFn: () => staffAPI.submitNightworkRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      queryClient.invalidateQueries({ queryKey: ['nightwork-request', requestId] });
      toast.success('Request submitted for approval');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to submit'),
  });

  const approveMutation = useMutation({
    mutationFn: (data) => staffAPI.approveNightworkRequest(requestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      queryClient.invalidateQueries({ queryKey: ['nightwork-request', requestId] });
      toast.success('Request approved');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: (data) => staffAPI.rejectNightworkRequest(requestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      queryClient.invalidateQueries({ queryKey: ['nightwork-request', requestId] });
      toast.success('Request rejected');
      setShowRejectForm(false);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to reject'),
  });

  const returnMutation = useMutation({
    mutationFn: (data) => staffAPI.returnNightworkRequest(requestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      queryClient.invalidateQueries({ queryKey: ['nightwork-request', requestId] });
      toast.warning('Request returned for amendment');
      setShowReturnForm(false);
      setReturnComments('');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to return request'),
  });

  const cancelMutation = useMutation({
    mutationFn: (data) => staffAPI.cancelNightworkRequest(requestId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      queryClient.invalidateQueries({ queryKey: ['nightwork-request', requestId] });
      toast.success('Request cancelled');
      setShowCancelForm(false);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to cancel'),
  });

  const amendMutation = useMutation({
    mutationFn: () => staffAPI.amendNightworkRequest(requestId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['nightwork'] });
      queryClient.invalidateQueries({ queryKey: ['nightwork-request', requestId] });
      toast.success('Amendment draft created');
      if (onEditAmendment && res.data?.amendment_id) {
        onEditAmendment(res.data.amendment_id);
      }
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to create amendment'),
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-800 mx-auto" />
        <p className="text-sm text-gray-500 mt-3">Loading request details...</p>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <p className="text-sm text-red-600">Failed to load request details.</p>
        <button onClick={onBack} className="btn btn-secondary mt-4">Go Back</button>
      </div>
    );
  }

  // Server-computed permissions are the single source of truth for
  // approval-chain actions — this keeps the UI from ever showing a button
  // the backend will 403 on (e.g. HR admin previously saw dead
  // Approve/Reject buttons since only the department's own Dy HOD/HOD are
  // real approvers; HR gets can_return/can_edit instead).
  const perms = request.permissions || {};
  const isOwner = currentUser?.staff_id === request.requested_by;
  const isDraft = request.status === 'draft';
  const isFrozen = request.status === 'frozen';
  const isAmendment = request.amendment_version > 1;
  const canCancel = perms.can_cancel;
  const canSubmit = isOwner && isDraft;
  const canAmend = perms.can_amend;
  // Bonus fix: any draft (plain v1 or amendment) is editable by its owner,
  // not just amendment drafts.
  const canEditDraft = isOwner && isDraft;
  const canApprove = perms.can_approve;
  const canReturn = perms.can_return;
  const canEditPending = perms.can_edit;
  const isHRAdmin = !!currentUser?.has_admin_access;
  const canHRAction = isHRAdmin && ['approved', 'frozen'].includes(request.status);
  const today = getTodayInSingaporeTimezone();
  const pastNightworkCount = (request.nightwork_dates || []).filter(d => d < today).length;
  const canHRCancel = pastNightworkCount < 2;

  const visibleModificationLogs = (request.modification_logs || []).filter(log => !isNoOpModification(log));

  const dateLabel = request.date_selection_type === 'range'
    ? formatDateRange(request.start_date, request.end_date)
    : (request.nightwork_dates || []).map(d => formatDate(d)).join(', ');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
        <div className="flex items-start gap-4">
          <button onClick={onBack} className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0 hover:bg-gray-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 mt-0.5">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-lg font-semibold text-gray-900">
                {request.request_number || (
                  <span className="italic text-gray-500">Draft (not yet submitted)</span>
                )}
              </h1>
              <StatusBadge status={request.status} showIcon />
              {request.amendment_version > 1 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-inset ring-purple-600/20">
                  v{request.amendment_version}
                </span>
              )}
              {request.is_backdated && (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-600/30"
                  title="The earliest covered date is in the past. See the request description for the reason."
                >
                  <Clock className="w-3 h-3" />
                  Backdated
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Requested by {request.requested_by_name} &middot; {formatDateTime(request.created_at)}
            </p>
          </div>
        </div>
      </div>

      {/* Frozen Banner */}
      {isFrozen && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">Amendment Pending</p>
            <p className="text-sm text-amber-700 mt-0.5">
              This request is frozen while an amendment is awaiting approval. The original schedule remains active until the amendment is approved or rejected.
            </p>
            {request.amendments?.length > 0 && onViewDetail && (
              <button
                onClick={() => onViewDetail(request.amendments[0].id)}
                className="mt-2 text-sm font-medium text-amber-800 hover:text-amber-900 inline-flex items-center gap-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
              >
                View Amendment <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Returned for Amendment Banner */}
      {isDraft && request.returned_for_amendment_reason && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <Undo2 className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900">
              Returned for Amendment
              {request.returned_for_amendment_reason.returned_at_level && (
                <span> by {request.returned_for_amendment_reason.by || 'Approver'} ({request.returned_for_amendment_reason.returned_at_level})</span>
              )}
            </p>
            {request.returned_for_amendment_reason.comments && (
              <p className="text-sm text-amber-700 mt-0.5 italic">&ldquo;{request.returned_for_amendment_reason.comments}&rdquo;</p>
            )}
            <p className="text-xs text-amber-600 mt-1">{formatDateTime(request.returned_for_amendment_reason.at)}</p>
          </div>
        </div>
      )}

      {/* Amendment Parent Link */}
      {isAmendment && request.amendment_of_id && onViewDetail && (
        <div className="bg-purple-50 border border-purple-200 rounded-2xl p-4 flex items-center gap-3">
          <GitBranch className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-purple-800">
              This is <span className="font-semibold">version {request.amendment_version}</span> — an amendment of{' '}
              <button
                onClick={() => onViewDetail(request.amendment_of_id)}
                className="font-semibold text-purple-900 underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
              >
                {request.amendment_of_number || 'the original request'}
              </button>
            </p>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Project & Description */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-blue-800" />
                Project & Scope
              </h3>
            </div>
            <div className="card-content space-y-4">
              <div>
                <p className="text-xs text-gray-500">Project</p>
                <p className="text-sm font-medium text-gray-900">{request.project_code} – {request.project_name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Description of Works</p>
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{request.description}</p>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-blue-800" />
                Schedule
              </h3>
            </div>
            <div className="card-content space-y-4">
              {/* Mode & Recovery — top of schedule for immediate visibility */}
              <div className="flex flex-wrap items-center gap-2">
                <InfoBadge
                  color={request.nightwork_mode === 'extension' ? 'violet' : 'slate'}
                  label={request.nightwork_mode === 'extension' ? 'Extension — after regular hours' : 'Standard'}
                />
                <InfoBadge
                  color={request.recovery_type === 'am_only' ? 'amber' : 'teal'}
                  label={request.recovery_type === 'am_only' ? 'AM-Only Recovery' : 'Full Day Recovery'}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Nightwork Dates</p>
                  <p className="text-sm text-gray-900">{dateLabel}</p>
                  {(request.nightwork_dates?.length || 0) > 1 && (
                    <p className="text-xs text-gray-500 mt-1">{request.nightwork_dates.length} nights</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500">Shift Time</p>
                  <p className="text-sm text-gray-900">{formatTime(request.start_time)} – {formatTime(request.end_time)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {request.shift_duration_hours}h gross &middot; {request.break_duration_minutes}min break &middot; {request.net_working_hours}h net
                  </p>
                </div>
                {request.recovery_dates?.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500">Recovery</p>
                    <p className="text-sm text-gray-900">
                      {request.recovery_dates.map((d) => formatDate(d)).join(', ')}
                    </p>
                    {request.recovery_label && (
                      <p className="text-xs text-gray-500 mt-1">{request.recovery_label}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <AssignedPersonnelSection
            assignments={request.assignments}
            request={request}
            scheduledDateCount={request.nightwork_dates?.length || request.specific_dates?.length || 0}
          />

          {/* Modification Log */}
          {visibleModificationLogs.length > 0 && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                  <History className="w-4 h-4 text-blue-800" />
                  Activity Log
                </h3>
              </div>
              <div className="card-content">
                <div className="space-y-3">
                  {visibleModificationLogs.map(log => {
                    const isHR = (
                      log.modification_type?.startsWith('hr_')
                      || log.details?.edited_by_hr_admin
                      || log.details?.returned_by_hr_admin
                    );
                    const reason = log.details?.reason || log.details?.comments;
                    const changedFields = log.modification_type === 'edited_pending' ? (log.details?.changed_fields || []) : [];
                    return (
                      <div key={log.id} className="flex gap-3">
                        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isHR ? 'bg-amber-400' : 'bg-blue-400'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isHR ? 'text-amber-900 font-medium' : 'text-gray-900'}`}>
                            {formatModificationType(log.modification_type)}
                            {log.modification_type === 'edited_pending' && log.details?.approval_reset && (
                              <span className="ml-1.5 text-xs font-normal text-amber-700">(approvals reset)</span>
                            )}
                          </p>
                          {changedFields.length > 0 && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              Changed: {changedFields.map(f => FIELD_LABELS[f] || f.replace(/_/g, ' ')).join(', ')}
                            </p>
                          )}
                          {reason && (
                            <p className="text-xs text-gray-600 mt-0.5 italic">&ldquo;{reason}&rdquo;</p>
                          )}
                          <p className="text-xs text-gray-500 mt-0.5">
                            {log.modified_by_name} &middot; {formatDateTime(log.created_at)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Approval Status + Actions */}
        <div className="space-y-6">
          {/* Approval Progress */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-sm font-semibold text-gray-900">Approval Progress</h3>
            </div>
            <div className="card-content space-y-4">
              <ApprovalStep
                label="Submitted"
                status="submitted"
                approver={request.requested_by_name}
                date={request.created_at}
              />
              {/* Hide Deputy HOD step when auto-skipped (no actual approver assigned) */}
              {!(request.deputy_hod_status === 'approved' && !request.deputy_hod_approver_name) && (
                <ApprovalStep
                  label={`Deputy HOD (${request.requested_by_department || 'Department'})`}
                  status={request.deputy_hod_status}
                  approver={request.deputy_hod_approver_name}
                  date={request.deputy_hod_date}
                  comments={request.deputy_hod_comments}
                />
              )}
              <ApprovalStep
                label={`HOD (${request.requested_by_department || 'Department'})`}
                status={request.hod_status}
                approver={request.hod_approver_name}
                date={request.hod_date}
                comments={request.hod_comments}
              />
              {request.rejection_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                  <p className="text-xs font-medium text-red-700">Rejection Reason</p>
                  <p className="text-sm text-red-600 mt-1">{request.rejection_reason}</p>
                </div>
              )}
              {request.cancellation_reason && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-2">
                  <p className="text-xs font-medium text-gray-700">Cancellation Reason</p>
                  <p className="text-sm text-gray-600 mt-1">{request.cancellation_reason}</p>
                  <p className="text-xs text-gray-500 mt-1">by {request.cancelled_by_name} &middot; {formatDateTime(request.cancelled_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          {(canSubmit || canApprove || canCancel || canAmend || canEditDraft || canEditPending || canReturn) && (
            <div className="card">
              <div className="card-header">
                <h3 className="text-sm font-semibold text-gray-900">Actions</h3>
              </div>
              <div className="card-content space-y-3">
                {canAmend && (
                  <button
                    onClick={() => amendMutation.mutate()}
                    disabled={amendMutation.isPending}
                    className="btn btn-primary w-full"
                  >
                    <Edit className="w-4 h-4" />
                    {amendMutation.isPending ? 'Creating Amendment...' : 'Amend Request'}
                  </button>
                )}

                {canEditDraft && onEditAmendment && (
                  <button
                    onClick={() => onEditAmendment(requestId)}
                    className="btn btn-primary w-full"
                  >
                    <Edit className="w-4 h-4" />
                    {isAmendment ? 'Edit Amendment' : 'Edit Draft'}
                  </button>
                )}

                {canEditPending && onEditAmendment && (
                  <div>
                    <button
                      onClick={() => onEditAmendment(requestId)}
                      className="btn btn-primary w-full"
                    >
                      <Edit className="w-4 h-4" />
                      Edit Request
                    </button>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Description-only edits keep current approvals. Changing dates, times, personnel, mode, recovery, or project resets approvals and restarts the approval chain.
                    </p>
                  </div>
                )}

                {canSubmit && (
                  <button
                    onClick={() => submitMutation.mutate()}
                    disabled={submitMutation.isPending}
                    className="btn btn-primary w-full"
                  >
                    <Send className="w-4 h-4" />
                    {submitMutation.isPending ? 'Submitting...' : 'Submit for Approval'}
                  </button>
                )}

                {canApprove && !showRejectForm && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Comments (optional)</label>
                      <textarea
                        rows={2}
                        className="input w-full resize-none text-sm"
                        placeholder="Add comments..."
                        value={approvalComments}
                        onChange={e => setApprovalComments(e.target.value)}
                      />
                    </div>
                    <div className="flex gap-2 btn-row-spread">
                      <button
                        onClick={() => setShowRejectForm(true)}
                        className="btn btn-danger"
                      >
                        <XCircle className="w-4 h-4" />
                        Reject
                      </button>
                      <button
                        onClick={() => approveMutation.mutate({ comments: approvalComments })}
                        disabled={approveMutation.isPending}
                        className="btn btn-success"
                      >
                        <CheckCircle className="w-4 h-4" />
                        {approveMutation.isPending ? 'Approving...' : 'Approve'}
                      </button>
                    </div>
                  </>
                )}

                {showRejectForm && (
                  <div className="space-y-2">
                    <label className="block text-xs text-gray-500">Reason for rejection</label>
                    <textarea
                      rows={3}
                      className="input w-full resize-none text-sm"
                      placeholder="Explain why this request is being rejected..."
                      value={rejectionReason}
                      onChange={e => setRejectionReason(e.target.value)}
                    />
                    <div className="flex gap-2 btn-row-spread">
                      <button onClick={() => setShowRejectForm(false)} className="btn btn-secondary">
                        Cancel
                      </button>
                      <button
                        onClick={() => rejectMutation.mutate({ reason: rejectionReason, comments: rejectionReason })}
                        disabled={!rejectionReason.trim() || rejectMutation.isPending}
                        className="btn btn-danger"
                      >
                        {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Reject'}
                      </button>
                    </div>
                  </div>
                )}

                {canReturn && !showReturnForm && (
                  <button onClick={() => setShowReturnForm(true)} className="btn btn-warning w-full">
                    <Undo2 className="w-4 h-4" />
                    Return for Amendment
                  </button>
                )}

                {showReturnForm && (
                  <div className="space-y-2">
                    <label className="block text-xs text-gray-500">Comments (required) — explain what needs to change</label>
                    <textarea
                      rows={3}
                      className="input w-full resize-none text-sm"
                      placeholder="Explain what the requester needs to amend..."
                      value={returnComments}
                      onChange={e => setReturnComments(e.target.value)}
                    />
                    <div className="flex gap-2 btn-row-spread">
                      <button onClick={() => setShowReturnForm(false)} className="btn btn-secondary">
                        Cancel
                      </button>
                      <button
                        onClick={() => returnMutation.mutate({ comments: returnComments })}
                        disabled={!returnComments.trim() || returnMutation.isPending}
                        className="btn btn-warning"
                      >
                        {returnMutation.isPending ? 'Returning...' : 'Confirm Return'}
                      </button>
                    </div>
                  </div>
                )}

                {canCancel && !showCancelForm && (
                  <button onClick={() => setShowCancelForm(true)} className="btn btn-danger w-full">
                    <Ban className="w-4 h-4" />
                    Cancel Request
                  </button>
                )}

                {showCancelForm && (
                  <div className="space-y-2">
                    <label className="block text-xs text-gray-500">Reason for cancellation</label>
                    <textarea
                      rows={2}
                      className="input w-full resize-none text-sm"
                      placeholder="Explain why this request is being cancelled..."
                      value={cancelReason}
                      onChange={e => setCancelReason(e.target.value)}
                    />
                    <div className="flex gap-2 btn-row-spread">
                      <button onClick={() => setShowCancelForm(false)} className="btn btn-secondary">
                        Back
                      </button>
                      <button
                        onClick={() => cancelMutation.mutate({ reason: cancelReason })}
                        disabled={!cancelReason.trim() || cancelMutation.isPending}
                        className="btn btn-danger"
                      >
                        {cancelMutation.isPending ? 'Cancelling...' : 'Confirm Cancel'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* HR Administrative Actions */}
          {canHRAction && (
            <div className="bg-white rounded-2xl shadow-lg border border-amber-300 overflow-hidden">
              <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
                <h3 className="text-sm font-semibold text-amber-900 flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-amber-700" />
                  HR Administrative Actions
                </h3>
                <p className="text-xs text-amber-700 mt-1">
                  These actions take effect immediately without approval.
                </p>
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Workers</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setHrActionType('add_workers')} className="btn btn-outline py-1.5 px-3 text-xs">
                      <UserPlus className="w-3.5 h-3.5 text-blue-700" /> Add
                    </button>
                    <button onClick={() => setHrActionType('remove_workers')} className="btn btn-outline py-1.5 px-3 text-xs">
                      <UserMinus className="w-3.5 h-3.5 text-red-600" /> Remove
                    </button>
                    <button onClick={() => setHrActionType('swap_worker')} className="btn btn-outline py-1.5 px-3 text-xs">
                      <ArrowLeftRight className="w-3.5 h-3.5 text-purple-600" /> Swap
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Safety Personnel</p>
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => setHrActionType('add_safety')} className="btn btn-outline py-1.5 px-3 text-xs">
                      <UserPlus className="w-3.5 h-3.5 text-green-700" /> Add
                    </button>
                    <button onClick={() => setHrActionType('remove_safety')} className="btn btn-outline py-1.5 px-3 text-xs">
                      <UserMinus className="w-3.5 h-3.5 text-red-600" /> Remove
                    </button>
                    <button onClick={() => setHrActionType('swap_safety')} className="btn btn-outline py-1.5 px-3 text-xs">
                      <ArrowLeftRight className="w-3.5 h-3.5 text-purple-600" /> Swap
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Schedule</p>
                  <button onClick={() => setHrActionType('adjust_schedule')} className="btn btn-outline py-1.5 px-3 text-xs">
                    <Calendar className="w-3.5 h-3.5 text-blue-700" /> Adjust Dates & Times
                  </button>
                </div>

                {canHRCancel && (
                  <>
                    <hr className="border-gray-200" />

                    <button onClick={() => setHrActionType('hr_cancel')} className="btn btn-danger w-full">
                      <Ban className="w-4 h-4" /> Cancel Request
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* HR Action Modal */}
      <NightworkHRActionModal
        isOpen={!!hrActionType}
        onClose={() => setHrActionType(null)}
        actionType={hrActionType}
        request={request}
        requestId={requestId}
      />
    </div>
  );
};

const ApprovalStep = ({ label, status, approver, date, comments }) => {
  const icons = {
    pending: <Clock className="w-4 h-4 text-gray-400" />,
    submitted: <Send className="w-4 h-4 text-blue-500" />,
    approved: <CheckCircle className="w-4 h-4 text-green-500" />,
    rejected: <XCircle className="w-4 h-4 text-red-500" />,
  };

  const bgColors = {
    pending: 'bg-gray-50',
    submitted: 'bg-blue-50',
    approved: 'bg-green-50',
    rejected: 'bg-red-50',
  };

  const badgeStatus = status === 'submitted' ? 'submitted' : (status === 'pending' ? 'pending' : status);

  return (
    <div className={`rounded-lg p-3 ${bgColors[status] || 'bg-gray-50'}`}>
      <div className="flex items-center gap-2">
        {icons[status] || icons.pending}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">{label}</p>
          {approver && <p className="text-xs text-gray-500">{approver}</p>}
        </div>
        <StatusBadge status={badgeStatus} />
      </div>
      {date && <p className="text-xs text-gray-500 mt-1 ml-6">{formatDateTime(date)}</p>}
      {comments && <p className="text-xs text-gray-600 mt-1 ml-6 italic">"{comments}"</p>}
    </div>
  );
};

const FIELD_LABELS = {
  project: 'Project',
  nightwork_mode: 'Mode',
  recovery_type: 'Recovery',
  date_selection_type: 'Date type',
  start_date: 'Start date',
  end_date: 'End date',
  specific_dates: 'Specific dates',
  start_time: 'Start time',
  end_time: 'End time',
  description: 'Description',
  worker_ids: 'Workers',
  safety_ids: 'Safety personnel',
};

export default NightworkRequestDetail;
